import { createSelector } from "reselect";

import { ManagedEntity } from "types/generated";

import { IacTableStoreState } from "./types";
import { IacTableRow, IacTableServiceRowType } from "./makeIacTableDataSlice/types";

export const selectTableRows = createSelector(
  (state: IacTableStoreState) => state.groups,
  (state: IacTableStoreState) => state.resources,
  (state: IacTableStoreState) => state.resourcesIdsPerGroup,
  (state: IacTableStoreState) => state.groupsMeta,
  (groups, resources, resourcesIdsPerGroup, groupsMeta): IacTableRow[] => {
    const tableRows: IacTableRow[] = [];

    for (let i = 0; i < groups.length; i++) {
      const group = groups[i];
      const isGroupOpen = groupsMeta[group.id]?.isOpen;

      tableRows.push(group);

      if (isGroupOpen) {
        const resourceIds = resourcesIdsPerGroup[group.id];

        if (resourceIds?.length) {
          for (let j = 0; j < resourceIds.length; j++) {
            const resource = resources[resourceIds[j]];
            if (resource) {
              tableRows.push(resource);
            }
          }
        }

        if (groupsMeta[group.id]?.hasMore) {
          tableRows.push({
            id: `${group.id}-service-load-more`,
            serviceType: IacTableServiceRowType.LoadMoreResources,
            groupId: group.id,
          });
        }
      }
    }

    return tableRows;
  }
);

const selectTableRowsMap = createSelector(
  selectTableRows,
  (tableRows): Map<number, IacTableRow> => {
    const map = new Map();
    for (let i = 0; i < tableRows.length; i++) {
      map.set(i, tableRows[i]);
    }
    return map;
  }
);

export const selectTableRowByIndex = createSelector(
  selectTableRowsMap,
  (_, index: number) => index,
  (tableRowsMap, index: number): IacTableRow => {
    return tableRowsMap.get(index);
  }
);

export const selectHasGroupResources = createSelector(
  (state: IacTableStoreState, groupId: string) => state.resourcesIdsPerGroup[groupId],
  (resourcesIds): boolean => !!resourcesIds
);

export const selectResource = createSelector(
  (state: IacTableStoreState, resourceId: string) => state.resources[resourceId],
  (resource): ManagedEntity | undefined => resource
);
